import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Custom hook to manage page transitions with state persistence
 * @param {number} duration - Transition duration in milliseconds
 * @returns {Object} - Transition state and handler functions
 */
const usePageTransition = (duration = 600) => {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState("fadeIn");
  const [prevScrollPos, setPrevScrollPos] = useState({});
  const prevPathRef = useRef("");
  const transitioningRef = useRef(false);
  const timeoutRef = useRef(null);

  // Save the previous path when location changes
  useEffect(() => {
    if (displayLocation.pathname !== location.pathname) {
      prevPathRef.current = displayLocation.pathname;
    }
  }, [location, displayLocation]);

  // Save scroll position when location changes and start transition
  useEffect(() => {
    // Only start transition if the location has changed
    if (location !== displayLocation && !transitioningRef.current) {
      // Mark that we're in a transition to prevent multiple transitions
      transitioningRef.current = true;

      // Save current scroll position before transition
      setPrevScrollPos((prev) => ({
        ...prev,
        [displayLocation.pathname]: window.scrollY,
      }));

      // Start transition out
      setTransitionStage("fadeOut");
    }
  }, [location, displayLocation]);

  // Clean up any lingering timeouts when component unmounts
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle animation end to update display location
  const handleAnimationEnd = () => {
    if (transitionStage === "fadeOut") {
      // Immediately scroll to top before changing the display location
      window.scrollTo({
        top: 0,
        behavior: "auto", // Instant scroll, no smooth animation
      });

      // Change the location to display
      setDisplayLocation(location);

      // Start transition in
      setTransitionStage("fadeIn");
    } else if (transitionStage === "fadeIn") {
      // Reset transitioning flag when animation is complete
      transitioningRef.current = false;
    }
  };

  return {
    transitionStage,
    handleAnimationEnd,
    displayLocation,
    currentPath: displayLocation.pathname,
    previousPath: prevPathRef.current,
    isTransitioning: transitioningRef.current,
  };
};

export default usePageTransition;
